/**
 * tokenStore — module-level in-memory JWT store.
 *
 * Access token lives only in JS module memory — never in Web Storage.
 * XSS cannot reach it. The refresh token is HttpOnly cookie only.
 * Token is lost on page reload and re-hydrated by AuthProvider's silent refresh.
 */

let _token: string | null = null;

/** Store access token in module memory. */
export function setInMemoryToken(token: string): void {
  _token = token;
}

/** Read the current in-memory access token. Returns null if not set. */
export function getInMemoryToken(): string | null {
  return _token;
}

/** Clear the in-memory access token. */
export function clearInMemoryToken(): void {
  _token = null;
}

/**
 * Session hint — a non-sensitive localStorage flag marking "a session was
 * established in this browser". NOT a credential (no token value) — it only
 * gates the boot-time silent refresh so we skip the wasted /token/refresh
 * probe (and its 401) for users who never logged in or have logged out.
 *
 * Set on login success, cleared on logout and on any failed refresh.
 * The HttpOnly refresh cookie remains the source of truth — this is just a
 * client-readable signal that a cookie is likely present.
 */
const SESSION_HINT_KEY = 'auth:session';

/** Mark that a session exists in this browser (call after a successful login). */
export function setSessionHint(): void {
  try {
    localStorage.setItem(SESSION_HINT_KEY, '1');
  } catch {
    // Private mode / storage disabled — degrade to always attempting refresh.
  }
}

/** Remove the session hint (call on logout or failed refresh). */
export function clearSessionHint(): void {
  try {
    localStorage.removeItem(SESSION_HINT_KEY);
  } catch {
    // No-op when storage is unavailable.
  }
}

/**
 * Whether a session hint exists. Returns true when storage is unavailable so
 * we fall back to the original always-refresh behavior rather than locking the
 * user out of auto-login.
 */
export function hasSessionHint(): boolean {
  try {
    return localStorage.getItem(SESSION_HINT_KEY) === '1';
  } catch {
    return true;
  }
}

/**
 * Decode JWT payload without signature verification.
 * Used only for immediate client-side routing decisions (admin vs publisher) after login.
 * Returns null if token is malformed.
 */
export function decodeToken(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split('.')[1] ?? '';
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const json = atob(base64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}
